// src/pages/ActivitiesPage.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import SectionHeader from '../components/SectionHeader';
import SEO from '../components/SEO';
import '../styles/ActivitiesPage.css';

interface ImageData {
  url: string;
  id: string;
}

interface ContentBlock {
  type: 'paragraph' | 'images';
  content: string;
  order: number;
}

interface Activity {
  _id: string;
  title: string;
  date: string;
  location: string;
  content: ContentBlock[];
  status: string;
  createdAt: string;
  updatedAt: string;
}

const ActivitiesPage: React.FC = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>('all');

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://empower-future21-nodejs.vercel.app/api/activities');
      
      if (!response.ok) {
        throw new Error('Failed to fetch activities');
      }
      
      const data = await response.json();
      // Filter only published activities
      const publishedActivities = data.filter((activity: Activity) => 
        activity.status === 'published'
      );
      
      setActivities(publishedActivities);
    } catch (err) {
      setError('Failed to load activities. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Group activities by year for filtering
  const getActivityYears = () => {
    const years = activities.map(activity => 
      new Date(activity.date).getFullYear()
    );
    return Array.from(new Set(years)).sort((a, b) => b - a); // Most recent first
  };

  const filteredActivities = activeFilter === 'all' 
    ? activities 
    : activities.filter(activity => 
        new Date(activity.date).getFullYear() === Number(activeFilter)
      );

  // Get first paragraph and first image for preview
  const getActivityPreview = (activity: Activity) => {
    let previewText = '';
    let previewImage = '';
    
    // Find first paragraph
    const firstParagraph = activity.content.find(block => 
      block.type === 'paragraph' && block.content.trim() !== ''
    );
    
    if (firstParagraph) {
      previewText = firstParagraph.content;
      // Limit preview text to first 150 characters
      if (previewText.length > 150) {
        previewText = previewText.substring(0, 150) + '...';
      }
    }
    
    // Find first image
    const firstImageBlock = activity.content.find(block => 
      block.type === 'images' && block.content && block.content !== '[]'
    );
    
    if (firstImageBlock) {
      try {
        const images = JSON.parse(firstImageBlock.content) as ImageData[];
        if (images.length > 0) {
          previewImage = images[0].url;
        }
      } catch (e) {
        console.error('Error parsing image data', e);
      }
    }
    
    return { previewText, previewImage };
  };

  // Format date in a readable way
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <SEO
        title="Activities & Events"
        description="Explore our activities and events aimed at empowering women, youth, and communities through education and sustainable development."
        keywords="activities, events, community development, education, empowerment, workshop"
      />
      
      <Hero
        title="Our Activities & Events"
        subtitle="Empowering communities through education, skill development, and inclusive initiatives"
        smallHero={true}
      />
      
      <section className="activities-section">
        <div className="container">
          <SectionHeader
            title="Recent Activities"
            subtitle="Explore our recent work and community initiatives"
          />
          
          {loading ? (
            <div className="activities-loading">
              <div className="loading-spinner"></div>
              <p>Loading activities...</p>
            </div>
          ) : error ? (
            <div className="activities-error">
              <p>{error}</p>
            </div>
          ) : (
            <>
              <div className="activities-filters">
                <button 
                  className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveFilter('all')}
                >
                  All
                </button>
                {getActivityYears().map(year => (
                  <button 
                    key={year}
                    className={`filter-btn ${activeFilter === year.toString() ? 'active' : ''}`}
                    onClick={() => setActiveFilter(year.toString())}
                  >
                    {year}
                  </button>
                ))}
              </div>
              
              {filteredActivities.length === 0 ? (
                <div className="no-activities">
                  <p>No activities found for the selected filter.</p>
                </div>
              ) : (
                <div className="activities-grid">
                  {filteredActivities.map(activity => {
                    const { previewText, previewImage } = getActivityPreview(activity);
                    return (
                      <div className="activity-card" key={activity._id}>
                        {previewImage && (
                          <div className="activity-image">
                            <img src={previewImage} alt={activity.title} />
                          </div>
                        )}
                        <div className="activity-content">
                          <div className="activity-meta">
                            <span className="activity-date">{formatDate(activity.date)}</span>
                            <span className="activity-location">{activity.location}</span>
                          </div>
                          <h3 className="activity-title">{activity.title}</h3>
                          <p className="activity-preview">{previewText}</p>
                          <Link to={`/activities/${activity._id}`} className="activity-link">
                            Read More
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default ActivitiesPage;